/**
 * Everything a player can wear, and how they come to own it.
 *
 * Three rules shape this file:
 *
 *   Cosmetic only. Nothing here changes a score, a timer or a vote. An unlock is
 *   something to show off, never an edge.
 *
 *   Derived, not stored. Levels and achievements are worked out from the stats
 *   the games already record, so there is no second ledger to drift out of step
 *   and no new write path in eight different rooms. It also means solo games,
 *   which deliberately go unrecorded, cannot be farmed for unlocks.
 *
 *   Nobody loses anything. Every emoji, colour and plate that existed before
 *   unlocks came along stays free; the earned items are all new.
 *
 * Pure: the server feeds in a snapshot of one player's stats and gets back their
 * progress. The same function decides what the profile screen shows and what the
 * server will let them equip, so the two can never disagree.
 */

import type { ArcadeGameId } from './arcade.ts';
import { NEON_COLORS, PLAYER_EMOJI, PLAYER_PLATES, type GameId } from './common.ts';
import { currentSeason, type SeasonId } from './seasons.ts';

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

/** One player's lifetime figures across every game, as the server reads them. */
export interface StatsSnapshot {
  movieGames: number;
  movieLongestChain: number;
  moviesPlayed: number;

  quizGames: number;
  quizCorrect: number;
  quizBestStreak: number;

  quiplashGames: number;
  quiplashVotes: number;
  quiplashSuperQuips: number;
  quiplashVictories: number;

  fibbageGames: number;
  fibbageFooled: number;
  fibbageSuperLies: number;
  fibbageCorrect: number;
  fibbageVictories: number;

  enoughGames: number;
  enoughFooled: number;
  enoughRecognised: number;
  enoughVictories: number;

  waveGames: number;
  waveGuesses: number;
  /** 99 until they have guessed at all. */
  waveBestDistance: number;
  waveCluesGiven: number;
  waveVictories: number;

  obscurityGames: number;
  obscurityLegendary: number;
  obscurityOneInAMillion: number;
  obscurityVictories: number;

  superGames: number;
  superVotesReceived: number;
  superBestRound: number;
  superWithMajority: number;
  superVictories: number;
  /** Rounds where the room chose them outright. */
  superMajorityWins: number;

  quizAnswered: number;
  quiplashMatchupsWon: number;
  fibbageBestLieFooled: number;
  enoughCorrect: number;
  enoughTruths: number;
  obscurityAnswers: number;

  // From every finished game, whoever won it: see match_results.
  /** Top three in a game of three or more. */
  podiums: number;
  /** Last in a game of three or more, and still came back. */
  lastPlaces: number;
  /** Finished a game with eight or more people in it. */
  bigGames: number;
  /** Different people they have finished a game with. */
  peopleMet: number;
  /** The most times they have finished ahead of any one person. */
  bestRivalWins: number;
  /** The most games they have finished in a single game night. */
  bestNight: number;
  /**
   * Solo games that count for experience: at most SOLO_DAILY_CAP a day. They
   * earn a little XP and nothing else — no achievement or stat reads them.
   */
  soloGames: number;

  chameleonGames: number;
  chameleonEscapes: number;
  chameleonSpotted: number;
  chameleonVictories: number;

  hottakesGames: number;
  hottakesSpotOn: number;
  hottakesHottest: number;
  hottakesVictories: number;

  /** Team games their side won. */
  teamWins: number;

  /** Daily puzzles finished. */
  dailyPlays: number;
  /**
   * Experience from daily puzzles, already worked out per puzzle from the
   * streak it was played on (see `dailyXp`), since that is where the bonus lives.
   */
  dailyXp: number;
  /** The longest run of days in a row with a daily finished. */
  bestDailyStreak: number;
  /** Days on which they finished the original four (Quiz, Obscurity, Dial, Double Feature). */
  dailyFullSets: number;
  /** Days on which they finished every daily there is. */
  dailySweeps: number;

  /** Games finished during the event on now. Nought when none is. */
  seasonGames: number;

  /** The solo and duel games, each kept in one row of arcade_stats. */
  arcade: Partial<Record<ArcadeGameId, ArcadeTotals>>;
}

/** One player's record in one solo or duel game. */
export interface ArcadeTotals {
  /** Every game finished, alone or not. */
  games: number;
  /** Games against somebody: these count towards games played and wins. */
  duels: number;
  wins: number;
  /** The game's own best: longest streak, best score. */
  best: number;
  /** Named figures for achievements, summed, or the best of, by name (see ARCADE_COUNT_RULE). */
  counts: Record<string, number>;
}

/** How an arcade figure adds up over games: most are totals, a few keep the best. */
export const ARCADE_COUNT_RULE: Record<string, 'sum' | 'max' | 'min'> = {
  streak: 'max',
  longest: 'max',
  fewest: 'min',
  fastestMs: 'min',
};

const NO_ARCADE: ArcadeTotals = { games: 0, duels: 0, wins: 0, best: 0, counts: {} };

/** A player's record in one solo or duel game, zeros if they have never played it. */
export function arcadeOf(s: StatsSnapshot, game: ArcadeGameId): ArcadeTotals {
  return s.arcade?.[game] ?? NO_ARCADE;
}

function arcadeCount(s: StatsSnapshot, game: ArcadeGameId, key: string, empty = 0): number {
  return arcadeOf(s, game).counts[key] ?? empty;
}

function arcadeSum(s: StatsSnapshot, pick: (t: ArcadeTotals) => number): number {
  return Object.values(s.arcade ?? {}).reduce((sum, t) => sum + pick(t ?? NO_ARCADE), 0);
}

export const EMPTY_STATS: StatsSnapshot = {
  movieGames: 0,
  movieLongestChain: 0,
  moviesPlayed: 0,
  quizGames: 0,
  quizCorrect: 0,
  quizBestStreak: 0,
  quiplashGames: 0,
  quiplashVotes: 0,
  quiplashSuperQuips: 0,
  quiplashVictories: 0,
  fibbageGames: 0,
  fibbageFooled: 0,
  fibbageSuperLies: 0,
  fibbageCorrect: 0,
  fibbageVictories: 0,
  enoughGames: 0,
  enoughFooled: 0,
  enoughRecognised: 0,
  enoughVictories: 0,
  waveGames: 0,
  waveGuesses: 0,
  waveBestDistance: 99,
  waveCluesGiven: 0,
  waveVictories: 0,
  obscurityGames: 0,
  obscurityLegendary: 0,
  obscurityOneInAMillion: 0,
  obscurityVictories: 0,
  superGames: 0,
  superVotesReceived: 0,
  superBestRound: 0,
  superWithMajority: 0,
  superVictories: 0,
  superMajorityWins: 0,
  quizAnswered: 0,
  quiplashMatchupsWon: 0,
  fibbageBestLieFooled: 0,
  enoughCorrect: 0,
  enoughTruths: 0,
  obscurityAnswers: 0,
  podiums: 0,
  lastPlaces: 0,
  bigGames: 0,
  peopleMet: 0,
  bestRivalWins: 0,
  bestNight: 0,
  soloGames: 0,
  chameleonGames: 0,
  chameleonEscapes: 0,
  chameleonSpotted: 0,
  chameleonVictories: 0,
  hottakesGames: 0,
  hottakesSpotOn: 0,
  hottakesHottest: 0,
  hottakesVictories: 0,
  teamWins: 0,
  dailyPlays: 0,
  dailyXp: 0,
  bestDailyStreak: 0,
  dailyFullSets: 0,
  dailySweeps: 0,
  seasonGames: 0,
  arcade: {},
};

export function totalGames(s: StatsSnapshot): number {
  return (
    s.movieGames +
    s.quizGames +
    s.quiplashGames +
    s.fibbageGames +
    s.enoughGames +
    s.waveGames +
    s.obscurityGames +
    s.superGames +
    s.chameleonGames +
    s.hottakesGames +
    arcadeSum(s, (t) => t.duels)
  );
}

/**
 * Games won outright. The Movie Game and the Quiz keep no winner — one has a
 * loser instead, the other only a high score — so they do not add to this.
 */
export function totalVictories(s: StatsSnapshot): number {
  return (
    s.quiplashVictories +
    s.fibbageVictories +
    s.enoughVictories +
    s.waveVictories +
    s.obscurityVictories +
    s.superVictories +
    s.chameleonVictories +
    s.hottakesVictories +
    arcadeSum(s, (t) => t.wins)
  );
}

/** How many different games they have finished at least once. */
export function gamesTried(s: StatsSnapshot): number {
  return [
    s.movieGames,
    s.quizGames,
    s.quiplashGames,
    s.fibbageGames,
    s.enoughGames,
    s.waveGames,
    s.obscurityGames,
    s.superGames,
    s.chameleonGames,
    s.hottakesGames,
    ...Object.values(s.arcade ?? {}).map((t) => t?.games ?? 0),
  ].filter((n) => n > 0).length;
}

// ---------------------------------------------------------------------------
// Achievements
// ---------------------------------------------------------------------------

export type Tier = 'bronze' | 'silver' | 'gold' | 'platinum';

export interface Achievement {
  id: string;
  /** Shown as a title under the player's name once earned. */
  name: string;
  description: string;
  /** Which game it belongs to, or null for ones earned anywhere. */
  game: GameId | null;
  /** How far along they are, in the same units as `target`. */
  measure(s: StatsSnapshot): number;
  target: number;
  /**
   * Steps on one ladder share a family and climb bronze, silver, gold and, for
   * the truly dedicated, platinum. The
   * profile shows a family as one row at its next step, so the list reads as
   * goals rather than as sixty separate chores.
   */
  family?: string;
  tier?: Tier;
}

type Def = Omit<Achievement, 'game'>;

const anywhere = (...defs: Def[]): Achievement[] => defs.map((d) => ({ ...d, game: null }));
const inGame = (game: GameId, ...defs: Def[]): Achievement[] =>
  defs.map((d) => ({ ...d, game }));

/**
 * Specific moments rather than grinding, so each one tells a story — and a new
 * player can earn their first in a single evening. The ladders give regulars
 * something further to aim at. Ids never change once shipped: a title is stored
 * by id, and renaming one would take it off whoever is wearing it.
 */
export const ACHIEVEMENTS: Achievement[] = [
  ...anywhere(
    { id: 'first-night', name: 'First Night', description: 'Finish your first game', measure: totalGames, target: 1 },
    { id: 'regular', name: 'Regular', description: 'Finish 25 games', measure: totalGames, target: 25, family: 'games', tier: 'bronze' },
    { id: 'veteran', name: 'Veteran', description: 'Finish 50 games', measure: totalGames, target: 50, family: 'games', tier: 'silver' },
    { id: 'lifer', name: 'Lifer', description: 'Finish 100 games', measure: totalGames, target: 100, family: 'games', tier: 'gold' },
    { id: 'champion', name: 'Champion', description: 'Win a game', measure: totalVictories, target: 1, family: 'wins', tier: 'bronze' },
    { id: 'dynasty', name: 'Dynasty', description: 'Win 10 games', measure: totalVictories, target: 10, family: 'wins', tier: 'silver' },
    { id: 'legend', name: 'Legend', description: 'Win 30 games', measure: totalVictories, target: 30, family: 'wins', tier: 'gold' },
    { id: 'grand-tour', name: 'Grand Tour', description: 'Finish ten different games', measure: gamesTried, target: 10 },
    { id: 'podium', name: 'On the Podium', description: 'Finish in the top three 10 times', measure: (s) => s.podiums, target: 10, family: 'podium', tier: 'bronze' },
    { id: 'fixture', name: 'Fixture', description: 'Finish in the top three 50 times', measure: (s) => s.podiums, target: 50, family: 'podium', tier: 'gold' },
    { id: 'social-butterfly', name: 'Social Butterfly', description: 'Finish games with 10 different people', measure: (s) => s.peopleMet, target: 10, family: 'people', tier: 'bronze' },
    { id: 'party-animal', name: 'Party Animal', description: 'Finish games with 30 different people', measure: (s) => s.peopleMet, target: 30, family: 'people', tier: 'silver' },
    { id: 'nemesis', name: 'Nemesis', description: 'Finish ahead of the same person 10 times', measure: (s) => s.bestRivalWins, target: 10 },
    { id: 'full-house', name: 'Full House', description: 'Finish a game with 8 or more players', measure: (s) => s.bigGames, target: 1 },
    { id: 'night-owl', name: 'Night Owl', description: 'Play 3 games in one game night', measure: (s) => s.bestNight, target: 3, family: 'night', tier: 'bronze' },
    { id: 'marathon', name: 'Marathon', description: 'Play 8 games in one game night', measure: (s) => s.bestNight, target: 8, family: 'night', tier: 'silver' },
    { id: 'good-sport', name: 'Good Sport', description: 'Finish last 10 times and keep coming back', measure: (s) => s.lastPlaces, target: 10 },
    { id: 'team-player', name: 'Team Player', description: 'Win a game in teams', measure: (s) => s.teamWins, target: 1, family: 'team', tier: 'bronze' },
    { id: 'dream-team', name: 'Dream Team', description: 'Win 10 games in teams', measure: (s) => s.teamWins, target: 10, family: 'team', tier: 'silver' },
    { id: 'habit-forming', name: 'Habit Forming', description: 'Play the daily 3 days in a row', measure: (s) => s.bestDailyStreak, target: 3, family: 'daily-streak', tier: 'bronze' },
    { id: 'week-warrior', name: 'Week Warrior', description: 'Play the daily 7 days in a row', measure: (s) => s.bestDailyStreak, target: 7, family: 'daily-streak', tier: 'silver' },
    { id: 'devoted', name: 'Devoted', description: 'Play the daily 30 days in a row', measure: (s) => s.bestDailyStreak, target: 30, family: 'daily-streak', tier: 'gold' },
    { id: 'full-set', name: 'Full Set', description: 'Finish the Quiz, Obscurity, Dial and Double Feature dailies in one day', measure: (s) => s.dailyFullSets, target: 1 },
    { id: 'clean-sweep', name: 'Clean Sweep', description: 'Finish all nine dailies in one day', measure: (s) => s.dailySweeps, target: 1 },
    { id: 'daily-regular', name: 'Daily Regular', description: 'Finish 50 daily puzzles', measure: (s) => s.dailyPlays, target: 50 },
    // Platinum: above gold, for the people who never leave.
    { id: 'hall-of-fame', name: 'Hall of Fame', description: 'Finish 250 games', measure: totalGames, target: 250, family: 'games', tier: 'platinum' },
    { id: 'immortal', name: 'Immortal', description: 'Win 75 games', measure: totalVictories, target: 75, family: 'wins', tier: 'platinum' },
    { id: 'monument', name: 'Monument', description: 'Finish in the top three 150 times', measure: (s) => s.podiums, target: 150, family: 'podium', tier: 'platinum' },
    { id: 'life-of-the-party', name: 'Life of the Party', description: 'Finish games with 60 different people', measure: (s) => s.peopleMet, target: 60, family: 'people', tier: 'gold' },
    { id: 'all-nighter', name: 'All-Nighter', description: 'Play 12 games in one game night', measure: (s) => s.bestNight, target: 12, family: 'night', tier: 'gold' },
    { id: 'unbreakable', name: 'Unbreakable', description: 'Play the daily 100 days in a row', measure: (s) => s.bestDailyStreak, target: 100, family: 'daily-streak', tier: 'platinum' },
    { id: 'superteam', name: 'Superteam', description: 'Win 30 games in teams', measure: (s) => s.teamWins, target: 30, family: 'team', tier: 'gold' },
  ),

  ...inGame(
    'movie',
    { id: 'chain-gang', name: 'Chain Gang', description: 'Play in a chain of 20 films', measure: (s) => s.movieLongestChain, target: 20, family: 'chain', tier: 'bronze' },
    { id: 'epic', name: 'Epic', description: 'Play in a chain of 40 films', measure: (s) => s.movieLongestChain, target: 40, family: 'chain', tier: 'silver' },
    { id: 'film-buff', name: 'Film Buff', description: 'Name 100 films', measure: (s) => s.moviesPlayed, target: 100, family: 'films', tier: 'bronze' },
    { id: 'cinephile', name: 'Cinephile', description: 'Name 500 films', measure: (s) => s.moviesPlayed, target: 500, family: 'films', tier: 'gold' },
    { id: 'auteur', name: 'Auteur', description: 'Name 1,500 films', measure: (s) => s.moviesPlayed, target: 1500, family: 'films', tier: 'platinum' },
    { id: 'saga', name: 'Saga', description: 'Play in a chain of 75 films', measure: (s) => s.movieLongestChain, target: 75, family: 'chain', tier: 'gold' },
  ),

  ...inGame(
    'quiz',
    { id: 'on-a-roll', name: 'On a Roll', description: 'Get 5 questions right in a row', measure: (s) => s.quizBestStreak, target: 5, family: 'streak', tier: 'bronze' },
    { id: 'unstoppable', name: 'Unstoppable', description: 'Get 12 questions right in a row', measure: (s) => s.quizBestStreak, target: 12, family: 'streak', tier: 'silver' },
    { id: 'know-it-all', name: 'Know-It-All', description: 'Answer 100 questions correctly', measure: (s) => s.quizCorrect, target: 100, family: 'correct', tier: 'bronze' },
    { id: 'mastermind', name: 'Mastermind', description: 'Answer 500 questions correctly', measure: (s) => s.quizCorrect, target: 500, family: 'correct', tier: 'gold' },
    { id: 'oracle', name: 'Oracle', description: 'Answer 1,500 questions correctly', measure: (s) => s.quizCorrect, target: 1500, family: 'correct', tier: 'platinum' },
    { id: 'untouchable', name: 'Untouchable', description: 'Get 20 questions right in a row', measure: (s) => s.quizBestStreak, target: 20, family: 'streak', tier: 'gold' },
  ),

  ...inGame(
    'quiplash',
    { id: 'quiplash', name: 'Quiplash!', description: 'Win a matchup with every single vote', measure: (s) => s.quiplashSuperQuips, target: 1, family: 'quips', tier: 'bronze' },
    { id: 'hat-trick', name: 'Hat-Trick', description: 'Land 3 Quiplashes', measure: (s) => s.quiplashSuperQuips, target: 3, family: 'quips', tier: 'silver' },
    { id: 'crowd-pleaser', name: 'Crowd Pleaser', description: 'Win 50 votes for your answers', measure: (s) => s.quiplashVotes, target: 50, family: 'laughs', tier: 'bronze' },
    { id: 'headliner', name: 'Headliner', description: 'Win 250 votes for your answers', measure: (s) => s.quiplashVotes, target: 250, family: 'laughs', tier: 'gold' },
    { id: 'comedy-legend', name: 'Comedy Legend', description: 'Win 750 votes for your answers', measure: (s) => s.quiplashVotes, target: 750, family: 'laughs', tier: 'platinum' },
    { id: 'quiplash-king', name: 'Quiplash Royalty', description: 'Land 10 Quiplashes', measure: (s) => s.quiplashSuperQuips, target: 10, family: 'quips', tier: 'gold' },
    { id: 'matchup-master', name: 'Matchup Master', description: 'Win 25 matchups', measure: (s) => s.quiplashMatchupsWon, target: 25 },
    { id: 'comedian', name: 'Comedian', description: 'Win 5 games of Quiplash', measure: (s) => s.quiplashVictories, target: 5 },
  ),

  ...inGame(
    'fibbage',
    { id: 'super-liar', name: 'Super Liar', description: 'Land a Super Lie', measure: (s) => s.fibbageSuperLies, target: 1 },
    { id: 'master-of-deceit', name: 'Master of Deceit', description: 'Fool 50 players with your lies', measure: (s) => s.fibbageFooled, target: 50, family: 'deceit', tier: 'bronze' },
    { id: 'con-artist', name: 'Con Artist', description: 'Fool 150 players with your lies', measure: (s) => s.fibbageFooled, target: 150, family: 'deceit', tier: 'gold' },
    { id: 'grand-deceiver', name: 'Grand Deceiver', description: 'Fool 400 players with your lies', measure: (s) => s.fibbageFooled, target: 400, family: 'deceit', tier: 'platinum' },
    { id: 'silver-tongue', name: 'Silver Tongue', description: 'Fool 4 people with a single lie', measure: (s) => s.fibbageBestLieFooled, target: 4 },
    { id: 'truth-seeker', name: 'Truth Seeker', description: 'Find the truth 25 times', measure: (s) => s.fibbageCorrect, target: 25, family: 'truth', tier: 'bronze' },
    { id: 'lie-detector', name: 'Lie Detector', description: 'Find the truth 100 times', measure: (s) => s.fibbageCorrect, target: 100, family: 'truth', tier: 'silver' },
    { id: 'polygraph', name: 'Polygraph', description: 'Find the truth 250 times', measure: (s) => s.fibbageCorrect, target: 250, family: 'truth', tier: 'gold' },
    { id: 'fibber', name: 'Fibber', description: 'Win 5 games of Fibbage', measure: (s) => s.fibbageVictories, target: 5 },
  ),

  ...inGame(
    'enough',
    { id: 'open-book', name: 'Open Book', description: 'Have your real answer spotted 10 times', measure: (s) => s.enoughRecognised, target: 10, family: 'known', tier: 'bronze' },
    { id: 'known-quantity', name: 'Known Quantity', description: 'Have your real answer spotted 50 times', measure: (s) => s.enoughRecognised, target: 50, family: 'known', tier: 'silver' },
    { id: 'two-faced', name: 'Two-Faced', description: 'Fool 25 players about somebody else', measure: (s) => s.enoughFooled, target: 25, family: 'faces', tier: 'bronze' },
    { id: 'shapeshifter', name: 'Shapeshifter', description: 'Fool 100 players about somebody else', measure: (s) => s.enoughFooled, target: 100, family: 'faces', tier: 'gold' },
    { id: 'thousand-faces', name: 'Thousand Faces', description: 'Fool 300 players about somebody else', measure: (s) => s.enoughFooled, target: 300, family: 'faces', tier: 'platinum' },
    { id: 'household-name', name: 'Household Name', description: 'Have your real answer spotted 150 times', measure: (s) => s.enoughRecognised, target: 150, family: 'known', tier: 'gold' },
    { id: 'mind-reader', name: 'Mind Reader', description: 'Spot the real answer 25 times', measure: (s) => s.enoughCorrect, target: 25 },
  ),

  ...inGame(
    'wavelengths',
    {
      id: 'bullseye',
      name: 'Bullseye',
      description: 'Land a guess dead on the target',
      // Best distance starts at 99, so a nought only means something once they have guessed.
      measure: (s) => (s.waveGuesses > 0 && s.waveBestDistance === 0 ? 1 : 0),
      target: 1,
    },
    { id: 'tuned-in', name: 'Tuned In', description: 'Give 20 clues', measure: (s) => s.waveCluesGiven, target: 20, family: 'clues', tier: 'bronze' },
    { id: 'broadcaster', name: 'Broadcaster', description: 'Give 75 clues', measure: (s) => s.waveCluesGiven, target: 75, family: 'clues', tier: 'silver' },
    { id: 'frequency', name: 'Frequency', description: 'Give 200 clues', measure: (s) => s.waveCluesGiven, target: 200, family: 'clues', tier: 'gold' },
    { id: 'wavelength-master', name: 'Master of Waves', description: 'Give 500 clues', measure: (s) => s.waveCluesGiven, target: 500, family: 'clues', tier: 'platinum' },
    { id: 'dial-twister', name: 'Dial Twister', description: 'Make 100 guesses', measure: (s) => s.waveGuesses, target: 100 },
    { id: 'same-wavelength', name: 'Same Wavelength', description: 'Win 5 games of Wavelengths', measure: (s) => s.waveVictories, target: 5 },
  ),

  ...inGame(
    'obscurity',
    { id: 'deep-cut', name: 'Deep Cut', description: 'Name a Legendary answer', measure: (s) => s.obscurityLegendary, target: 1, family: 'legendary', tier: 'bronze' },
    { id: 'obscurantist', name: 'Obscurantist', description: 'Name 10 Legendary answers', measure: (s) => s.obscurityLegendary, target: 10, family: 'legendary', tier: 'silver' },
    { id: 'archivist', name: 'Archivist', description: 'Name 50 Legendary answers', measure: (s) => s.obscurityLegendary, target: 50, family: 'legendary', tier: 'gold' },
    { id: 'curator', name: 'Curator', description: 'Name 150 Legendary answers', measure: (s) => s.obscurityLegendary, target: 150, family: 'legendary', tier: 'platinum' },
    { id: 'one-in-a-million', name: 'One in a Million', description: 'Name a One-in-a-Million answer', measure: (s) => s.obscurityOneInAMillion, target: 1 },
    { id: 'curious-mind', name: 'Curious Mind', description: 'Give 100 answers', measure: (s) => s.obscurityAnswers, target: 100 },
    { id: 'oddball', name: 'Oddball', description: 'Win 5 games of Obscurity', measure: (s) => s.obscurityVictories, target: 5 },
  ),

  ...inGame(
    'superlatives',
    { id: 'usual-suspect', name: 'The Usual Suspect', description: 'Be voted for 25 times', measure: (s) => s.superVotesReceived, target: 25, family: 'suspect', tier: 'bronze' },
    { id: 'main-character', name: 'Main Character', description: 'Be voted for 100 times', measure: (s) => s.superVotesReceived, target: 100, family: 'suspect', tier: 'gold' },
    { id: 'icon', name: 'Icon', description: 'Be voted for 300 times', measure: (s) => s.superVotesReceived, target: 300, family: 'suspect', tier: 'platinum' },
    { id: 'landslide', name: 'Landslide', description: 'Take 5 votes in a single round', measure: (s) => s.superBestRound, target: 5 },
    { id: 'hive-mind', name: 'Hive Mind', description: 'Go with the room 25 times', measure: (s) => s.superWithMajority, target: 25, family: 'hive', tier: 'bronze' },
    { id: 'room-reader', name: 'Room Reader', description: 'Go with the room 100 times', measure: (s) => s.superWithMajority, target: 100, family: 'hive', tier: 'silver' },
    { id: 'collective', name: 'The Collective', description: 'Go with the room 250 times', measure: (s) => s.superWithMajority, target: 250, family: 'hive', tier: 'gold' },
    { id: 'crowd-favourite', name: 'Crowd Favourite', description: "Be the room's pick in 10 rounds", measure: (s) => s.superMajorityWins, target: 10 },
  ),

  ...inGame(
    'chameleon',
    { id: 'master-of-disguise', name: 'Master of Disguise', description: 'Get away with it as the chameleon', measure: (s) => s.chameleonEscapes, target: 1, family: 'escapes', tier: 'bronze' },
    { id: 'invisible', name: 'Invisible', description: 'Get away with it 10 times', measure: (s) => s.chameleonEscapes, target: 10, family: 'escapes', tier: 'silver' },
    { id: 'eagle-eye', name: 'Eagle Eye', description: 'Vote for the chameleon 10 times', measure: (s) => s.chameleonSpotted, target: 10, family: 'spotted', tier: 'bronze' },
    { id: 'hawk-eye', name: 'Hawk Eye', description: 'Vote for the chameleon 50 times', measure: (s) => s.chameleonSpotted, target: 50, family: 'spotted', tier: 'gold' },
    { id: 'phantom', name: 'Phantom', description: 'Get away with it 25 times', measure: (s) => s.chameleonEscapes, target: 25, family: 'escapes', tier: 'gold' },
    { id: 'nobody-at-all', name: 'Nobody At All', description: 'Get away with it 60 times', measure: (s) => s.chameleonEscapes, target: 60, family: 'escapes', tier: 'platinum' },
    { id: 'all-seeing', name: 'All-Seeing', description: 'Vote for the chameleon 150 times', measure: (s) => s.chameleonSpotted, target: 150, family: 'spotted', tier: 'platinum' },
    { id: 'lizard-king', name: 'Lizard King', description: 'Win 5 games of Chameleon', measure: (s) => s.chameleonVictories, target: 5, family: 'cham-wins', tier: 'bronze' },
    { id: 'cold-blooded', name: 'Cold-Blooded', description: 'Win 20 games of Chameleon', measure: (s) => s.chameleonVictories, target: 20, family: 'cham-wins', tier: 'gold' },
  ),

  ...inGame(
    'hottakes',
    { id: 'mind-meld', name: 'Mind Meld', description: 'Guess spot on 10 times', measure: (s) => s.hottakesSpotOn, target: 10, family: 'spot-on', tier: 'bronze' },
    { id: 'pollster', name: 'Pollster', description: 'Guess spot on 50 times', measure: (s) => s.hottakesSpotOn, target: 50, family: 'spot-on', tier: 'silver' },
    { id: 'spicy-take', name: 'Spicy Take', description: 'Have the hottest take in the room 10 times', measure: (s) => s.hottakesHottest, target: 10 },
    { id: 'pundit', name: 'Pundit', description: 'Guess spot on 120 times', measure: (s) => s.hottakesSpotOn, target: 120, family: 'spot-on', tier: 'gold' },
    { id: 'prophet', name: 'Prophet', description: 'Guess spot on 300 times', measure: (s) => s.hottakesSpotOn, target: 300, family: 'spot-on', tier: 'platinum' },
    { id: 'contrarian', name: 'Contrarian', description: 'Have the hottest take in the room 40 times', measure: (s) => s.hottakesHottest, target: 40 },
    { id: 'hot-shot', name: 'Hot Shot', description: 'Win 5 games of Hot Takes', measure: (s) => s.hottakesVictories, target: 5, family: 'hot-wins', tier: 'bronze' },
    { id: 'firestarter', name: 'Firestarter', description: 'Win 20 games of Hot Takes', measure: (s) => s.hottakesVictories, target: 20, family: 'hot-wins', tier: 'gold' },
  ),
  ...inGame(
    'higherlower',
    { id: 'on-the-run', name: 'On the Run', description: 'A run of 10 in Higher or Lower', measure: (s) => arcadeOf(s, 'higherlower').best, target: 10, family: 'hl-run', tier: 'bronze' },
    { id: 'film-historian', name: 'Film Historian', description: 'A run of 25 in Higher or Lower', measure: (s) => arcadeOf(s, 'higherlower').best, target: 25, family: 'hl-run', tier: 'silver' },
    { id: 'chronologist', name: 'Chronologist', description: 'A run of 50 in Higher or Lower', measure: (s) => arcadeOf(s, 'higherlower').best, target: 50, family: 'hl-run', tier: 'gold' },
  ),
  ...inGame(
    'castreveal',
    { id: 'name-drop', name: 'Name Drop', description: 'Name 10 films in Cast Reveal', measure: (s) => arcadeCount(s, 'castreveal', 'solved'), target: 10, family: 'cast-solved', tier: 'bronze' },
    { id: 'casting-director', name: 'Casting Director', description: 'Name 75 films in Cast Reveal', measure: (s) => arcadeCount(s, 'castreveal', 'solved'), target: 75, family: 'cast-solved', tier: 'gold' },
    { id: 'first-billing', name: 'First Billing', description: 'Name a film from its first clue in Cast Reveal', measure: (s) => arcadeCount(s, 'castreveal', 'quick'), target: 1 },
  ),
  ...inGame(
    'posterzoom',
    { id: 'sharp-eyed', name: 'Sharp-Eyed', description: 'Name a poster in Poster Zoom within three seconds', measure: (s) => arcadeCount(s, 'posterzoom', 'quick'), target: 1 },
    { id: 'poster-child', name: 'Poster Child', description: 'Name 10 posters in Poster Zoom', measure: (s) => arcadeCount(s, 'posterzoom', 'solved'), target: 10, family: 'poster-solved', tier: 'bronze' },
    { id: 'box-office', name: 'Box Office', description: 'Name 75 posters in Poster Zoom', measure: (s) => arcadeCount(s, 'posterzoom', 'solved'), target: 75, family: 'poster-solved', tier: 'gold' },
  ),
  ...inGame(
    'sixdegrees',
    { id: 'connector', name: 'Connector', description: 'Connect 5 pairs in Six Degrees', measure: (s) => arcadeCount(s, 'sixdegrees', 'connected'), target: 5, family: 'six-connect', tier: 'bronze' },
    { id: 'bacon-number', name: 'Bacon Number', description: 'Connect in par 10 times in Six Degrees', measure: (s) => arcadeCount(s, 'sixdegrees', 'atPar'), target: 10, family: 'six-connect', tier: 'silver' },
  ),
  ...inGame(
    'connect4',
    { id: 'four-play', name: 'Four in a Row', description: 'Win 5 matches of Connect Four', measure: (s) => arcadeOf(s, 'connect4').wins, target: 5, family: 'c4-wins', tier: 'bronze' },
    { id: 'drop-zone', name: 'Drop Zone', description: 'Win 25 matches of Connect Four', measure: (s) => arcadeOf(s, 'connect4').wins, target: 25, family: 'c4-wins', tier: 'gold' },
  ),
  ...inGame(
    'noughts',
    { id: 'tic-tac-pro', name: 'Tic-Tac-Pro', description: 'Win 5 matches of Noughts & Crosses', measure: (s) => arcadeOf(s, 'noughts').wins, target: 5, family: 'oxo-wins', tier: 'bronze' },
    { id: 'ultimate-mind', name: 'Ultimate Mind', description: 'Win 25 matches of Noughts & Crosses', measure: (s) => arcadeOf(s, 'noughts').wins, target: 25, family: 'oxo-wins', tier: 'gold' },
  ),
  ...inGame(
    'battleships',
    { id: 'admiral', name: 'Admiral', description: 'Win 5 games of Battleships', measure: (s) => arcadeOf(s, 'battleships').wins, target: 5, family: 'bs-wins', tier: 'bronze' },
    { id: 'sharpshooter', name: 'Sharpshooter', description: 'Win Battleships with 10 misses or fewer', measure: (s) => arcadeCount(s, 'battleships', 'cleanWins'), target: 1 },
    { id: 'fleet-commander', name: 'Fleet Commander', description: 'Win 25 games of Battleships', measure: (s) => arcadeOf(s, 'battleships').wins, target: 25, family: 'bs-wins', tier: 'gold' },
  ),
  ...inGame(
    'dots',
    { id: 'box-clever', name: 'Box Clever', description: 'Win 5 games of Dots and Boxes', measure: (s) => arcadeOf(s, 'dots').wins, target: 5, family: 'dots-wins', tier: 'bronze' },
    { id: 'dot-matrix', name: 'Dot Matrix', description: 'Win 25 games of Dots and Boxes', measure: (s) => arcadeOf(s, 'dots').wins, target: 25, family: 'dots-wins', tier: 'gold' },
    { id: 'boxed-in', name: 'Boxed In', description: 'Claim 250 boxes in Dots and Boxes', measure: (s) => arcadeCount(s, 'dots', 'boxes'), target: 250, tier: 'silver' },
  ),
  ...inGame(
    'wordduel',
    { id: 'wordsmith', name: 'Wordsmith', description: 'Solve 10 words in Word Duel', measure: (s) => arcadeCount(s, 'wordduel', 'solved'), target: 10, family: 'word-solved', tier: 'bronze' },
    { id: 'lexicon', name: 'Lexicon', description: 'Solve 75 words in Word Duel', measure: (s) => arcadeCount(s, 'wordduel', 'solved'), target: 75, family: 'word-solved', tier: 'gold' },
    { id: 'in-two', name: 'In Two', description: 'Solve a word in two guesses', measure: (s) => (arcadeCount(s, 'wordduel', 'fewest', 99) <= 2 ? 1 : 0), target: 1 },
  ),
  ...inGame(
    'anagrams',
    { id: 'anagrammer', name: 'Anagrammer', description: 'Find 100 words in Anagram Race', measure: (s) => arcadeCount(s, 'anagrams', 'words'), target: 100, family: 'ana-words', tier: 'bronze' },
    { id: 'seven-up', name: 'Seven Up', description: 'Use all seven letters in Anagram Race', measure: (s) => arcadeCount(s, 'anagrams', 'longest'), target: 7 },
    { id: 'word-hoard', name: 'Word Hoard', description: 'Find 1,000 words in Anagram Race', measure: (s) => arcadeCount(s, 'anagrams', 'words'), target: 1000, family: 'ana-words', tier: 'gold' },
  ),
  ...inGame(
    'poker',
    { id: 'first-pot', name: 'First Pot', description: 'Win 10 hands of Poker', measure: (s) => arcadeCount(s, 'poker', 'handsWon'), target: 10, family: 'poker-hands', tier: 'bronze' },
    { id: 'card-shark', name: 'Card Shark', description: 'Win 3 games of Poker', measure: (s) => arcadeOf(s, 'poker').wins, target: 3, family: 'poker-wins', tier: 'silver' },
    { id: 'high-roller', name: 'High Roller', description: 'Win a pot of 2,000 chips or more', measure: (s) => arcadeOf(s, 'poker').best, target: 2000 },
    { id: 'full-boat', name: 'Full Boat', description: 'Win a showdown with a full house or better', measure: (s) => arcadeCount(s, 'poker', 'monsters'), target: 1 },
    { id: 'all-in', name: 'All In', description: 'Win 5 hands after going all in', measure: (s) => arcadeCount(s, 'poker', 'allInWins'), target: 5 },
    { id: 'chip-leader', name: 'Chip Leader', description: 'Win 100 hands of Poker', measure: (s) => arcadeCount(s, 'poker', 'handsWon'), target: 100, family: 'poker-hands', tier: 'gold' },
    { id: 'poker-legend', name: 'Poker Legend', description: 'Win 15 games of Poker', measure: (s) => arcadeOf(s, 'poker').wins, target: 15, family: 'poker-wins', tier: 'gold' },
  ),
  ...inGame(
    'liarsdice',
    { id: 'liar-liar', name: 'Liar, Liar', description: "Catch 10 lies in Liar's Dice", measure: (s) => arcadeCount(s, 'liarsdice', 'catches'), target: 10, family: 'liars-catch', tier: 'bronze' },
    { id: 'poker-face', name: 'Poker Face', description: 'Have 10 of your bids called a lie when they were true', measure: (s) => arcadeCount(s, 'liarsdice', 'survived'), target: 10 },
    { id: 'last-die', name: 'Last Die Standing', description: "Win Liar's Dice with one die left", measure: (s) => arcadeCount(s, 'liarsdice', 'clutch'), target: 1 },
    { id: 'dice-master', name: 'Dice Master', description: "Win 5 games of Liar's Dice", measure: (s) => arcadeOf(s, 'liarsdice').wins, target: 5, family: 'liars-wins', tier: 'silver' },
    { id: 'dice-legend', name: 'Dice Legend', description: "Win 25 games of Liar's Dice", measure: (s) => arcadeOf(s, 'liarsdice').wins, target: 25, family: 'liars-wins', tier: 'gold' },
    { id: 'human-polygraph', name: 'Human Polygraph', description: "Catch 75 lies in Liar's Dice", measure: (s) => arcadeCount(s, 'liarsdice', 'catches'), target: 75, family: 'liars-catch', tier: 'gold' },
  ),
  ...inGame(
    'whosaid',
    { id: 'people-reader', name: 'People Reader', description: 'Guess 25 authors right in Who Said It', measure: (s) => arcadeCount(s, 'whosaid', 'correct'), target: 25, family: 'whosaid-correct', tier: 'bronze' },
    { id: 'enigma', name: 'Enigma', description: 'Fool 25 guesses in Who Said It', measure: (s) => arcadeCount(s, 'whosaid', 'fooled'), target: 25 },
    { id: 'mystery-guest', name: 'Mystery Guest', description: 'Write an answer nobody placed', measure: (s) => arcadeCount(s, 'whosaid', 'mysteries'), target: 1 },
    { id: 'inner-circle', name: 'Inner Circle', description: 'Win 5 games of Who Said It', measure: (s) => arcadeOf(s, 'whosaid').wins, target: 5 },
    { id: 'psychic', name: 'Psychic', description: 'Guess 150 authors right in Who Said It', measure: (s) => arcadeCount(s, 'whosaid', 'correct'), target: 150, family: 'whosaid-correct', tier: 'gold' },
  ),
  ...inGame(
    'wouldyourather',
    { id: 'crowd-reader', name: 'Crowd Reader', description: 'Call the split exactly 10 times in Would You Rather', measure: (s) => arcadeCount(s, 'wouldyourather', 'exact'), target: 10, family: 'wyr-exact', tier: 'bronze' },
    { id: 'room-whisperer', name: 'Room Whisperer', description: 'Call the split exactly 50 times in Would You Rather', measure: (s) => arcadeCount(s, 'wouldyourather', 'exact'), target: 50, family: 'wyr-exact', tier: 'gold' },
    { id: 'odd-one-out', name: 'Odd One Out', description: 'Be the only one on your side 5 times', measure: (s) => arcadeCount(s, 'wouldyourather', 'alone'), target: 5 },
    { id: 'rather-good', name: 'Rather Good', description: 'Win 5 games of Would You Rather', measure: (s) => arcadeOf(s, 'wouldyourather').wins, target: 5 },
  ),
  ...inGame(
    'cheat',
    { id: 'caught-you', name: 'Caught You', description: 'Catch 10 cheats', measure: (s) => arcadeCount(s, 'cheat', 'catches'), target: 10, family: 'cheat-catch', tier: 'bronze' },
    { id: 'eagle-ears', name: 'Eagle Ears', description: 'Catch 75 cheats', measure: (s) => arcadeCount(s, 'cheat', 'catches'), target: 75, family: 'cheat-catch', tier: 'gold' },
    { id: 'smooth-liar', name: 'Smooth Liar', description: 'Get away with 25 lies in Cheat', measure: (s) => arcadeCount(s, 'cheat', 'getaways'), target: 25 },
    { id: 'clean-hands', name: 'Clean Hands', description: 'Win 3 games of Cheat', measure: (s) => arcadeOf(s, 'cheat').wins, target: 3, family: 'cheat-wins', tier: 'silver' },
    { id: 'cheat-champion', name: 'Cheat Champion', description: 'Win 15 games of Cheat', measure: (s) => arcadeOf(s, 'cheat').wins, target: 15, family: 'cheat-wins', tier: 'gold' },
  ),
  ...inGame(
    'rankem',
    { id: 'on-the-same-page', name: 'On the Same Page', description: 'Put 25 things in exactly the judge’s spot', measure: (s) => arcadeCount(s, 'rankem', 'exact'), target: 25, family: 'rank-exact', tier: 'bronze' },
    { id: 'telepath', name: 'Telepath', description: 'Put 150 things in exactly the judge’s spot', measure: (s) => arcadeCount(s, 'rankem', 'exact'), target: 150, family: 'rank-exact', tier: 'gold' },
    { id: 'perfect-order', name: 'Perfect Order', description: 'Match the judge’s whole ranking', measure: (s) => arcadeCount(s, 'rankem', 'perfect'), target: 1 },
    { id: 'predictable', name: 'Predictable', description: 'Have 15 people match your number one as judge', measure: (s) => arcadeCount(s, 'rankem', 'readable'), target: 15 },
  ),
  ...inGame(
    'spotlight',
    { id: 'good-friend', name: 'Good Friend', description: 'Predict 25 Spotlight answers right', measure: (s) => arcadeCount(s, 'spotlight', 'reads'), target: 25, family: 'spot-reads', tier: 'bronze' },
    { id: 'best-friend', name: 'Best Friend', description: 'Predict 150 Spotlight answers right', measure: (s) => arcadeCount(s, 'spotlight', 'reads'), target: 150, family: 'spot-reads', tier: 'gold' },
    { id: 'easy-read', name: 'Easy Read', description: 'Have everybody predict you right 10 times', measure: (s) => arcadeCount(s, 'spotlight', 'openBook'), target: 10 },
  ),
  ...inGame(
    'blindranking',
    { id: 'in-tune', name: 'In Tune', description: 'Be closest to the room in Blind Ranking 5 times', measure: (s) => arcadeCount(s, 'blindranking', 'inTune'), target: 5 },
    { id: 'against-the-grain', name: 'Against the Grain', description: 'Be furthest from the room in Blind Ranking 5 times', measure: (s) => arcadeCount(s, 'blindranking', 'contrarian'), target: 5 },
    { id: 'blind-faith', name: 'Blind Faith', description: 'Play 10 games of Blind Ranking', measure: (s) => arcadeOf(s, 'blindranking').games, target: 10 },
  ),
  ...inGame(
    'onestar',
    { id: 'harsh-critic', name: 'Harsh Critic', description: 'Get 25 votes for your one-star reviews', measure: (s) => arcadeCount(s, 'onestar', 'votes'), target: 25, family: 'onestar-votes', tier: 'bronze' },
    { id: 'poison-pen', name: 'Poison Pen', description: 'Get 150 votes for your one-star reviews', measure: (s) => arcadeCount(s, 'onestar', 'votes'), target: 150, family: 'onestar-votes', tier: 'gold' },
    { id: 'unanimous', name: 'Unanimous', description: 'Get every vote for a review', measure: (s) => arcadeCount(s, 'onestar', 'sweeps'), target: 1 },
  ),
  ...inGame(
    'wagerquiz',
    { id: 'high-stakes', name: 'High Stakes', description: 'Win a bet of 1,000 or more', measure: (s) => arcadeCount(s, 'wagerquiz', 'bigWin'), target: 1000 },
    { id: 'all-or-nothing', name: 'All or Nothing', description: 'Bet everything in the last round, and win', measure: (s) => arcadeCount(s, 'wagerquiz', 'allInWins'), target: 1 },
    { id: 'sure-thing', name: 'Sure Thing', description: 'Answer 50 Wager Quiz questions right', measure: (s) => arcadeCount(s, 'wagerquiz', 'correct'), target: 50 },
  ),
  ...inGame(
    'fakinit',
    { id: 'faker-finder', name: 'Faker Finder', description: 'Spot the Faker 10 times', measure: (s) => arcadeCount(s, 'fakinit', 'spotted'), target: 10, family: 'fake-spot', tier: 'bronze' },
    { id: 'lie-to-me', name: 'Lie to Me', description: 'Spot the Faker 60 times', measure: (s) => arcadeCount(s, 'fakinit', 'spotted'), target: 60, family: 'fake-spot', tier: 'gold' },
    { id: 'master-faker', name: 'Master Faker', description: 'Get away with faking 5 times', measure: (s) => arcadeCount(s, 'fakinit', 'escapes'), target: 5 },
  ),
  ...inGame(
    'undercover',
    { id: 'agent', name: 'Agent', description: 'Find the Undercover 10 times', measure: (s) => arcadeCount(s, 'undercover', 'found'), target: 10, family: 'uc-found', tier: 'bronze' },
    { id: 'spymaster', name: 'Spymaster', description: 'Find the Undercover 60 times', measure: (s) => arcadeCount(s, 'undercover', 'found'), target: 60, family: 'uc-found', tier: 'gold' },
    { id: 'deep-cover', name: 'Deep Cover', description: 'Survive a round as the Undercover', measure: (s) => arcadeCount(s, 'undercover', 'hidden'), target: 1 },
    { id: 'saw-it-coming', name: 'Saw It Coming', description: 'Get caught, then guess the others’ word', measure: (s) => arcadeCount(s, 'undercover', 'guessed'), target: 1 },
  ),
  ...inGame(
    'mole',
    { id: 'mole-hunter', name: 'Mole Hunter', description: 'Find the Mole 10 times', measure: (s) => arcadeCount(s, 'mole', 'found'), target: 10, family: 'mole-found', tier: 'bronze' },
    { id: 'whack-a-mole', name: 'Whack-a-Mole', description: 'Find the Mole 50 times', measure: (s) => arcadeCount(s, 'mole', 'found'), target: 50, family: 'mole-found', tier: 'gold' },
    { id: 'insider', name: 'Insider', description: 'Take the pot as the Mole', measure: (s) => arcadeCount(s, 'mole', 'heists'), target: 1 },
  ),
  ...inGame(
    'boxart',
    { id: 'insert-coin', name: 'Insert Coin', description: 'Name a game in Box Art Zoom within three seconds', measure: (s) => arcadeCount(s, 'boxart', 'quick'), target: 1 },
    { id: 'cartridge-collector', name: 'Cartridge Collector', description: 'Name 10 games in Box Art Zoom', measure: (s) => arcadeCount(s, 'boxart', 'solved'), target: 10, family: 'boxart-solved', tier: 'bronze' },
    { id: 'completionist', name: 'Completionist', description: 'Name 75 games in Box Art Zoom', measure: (s) => arcadeCount(s, 'boxart', 'solved'), target: 75, family: 'boxart-solved', tier: 'gold' },
  ),
  ...anywhere(
    { id: 'duellist', name: 'Duellist', description: 'Win 10 solo-and-duel games against somebody', measure: (s) => arcadeSum(s, (t) => t.wins), target: 10, family: 'duels', tier: 'bronze' },
    { id: 'arcade-ace', name: 'Arcade Ace', description: 'Win 100 solo-and-duel games against somebody', measure: (s) => arcadeSum(s, (t) => t.wins), target: 100, family: 'duels', tier: 'platinum' },
  ),
];

export function achievementById(id: string): Achievement | undefined {
  return ACHIEVEMENTS.find((a) => a.id === id);
}

// ---------------------------------------------------------------------------
// Experience and levels
// ---------------------------------------------------------------------------

export const XP_PER_GAME = 100;
export const XP_PER_VICTORY = 150;
export const XP_PER_ACHIEVEMENT = 250;

/** A solo game is practice: a quarter of a real one. */
export const XP_PER_SOLO = 25;
/** Solo games beyond this many in one day earn nothing, so XP cannot be farmed alone. */
export const SOLO_DAILY_CAP = 10;

/** The higher steps on a ladder are worth more, because they take more. */
export const XP_PER_TIER: Record<Tier, number> = { bronze: 250, silver: 400, gold: 600, platinum: 1000 };

export function xpFor(achievement: Achievement): number {
  return achievement.tier ? XP_PER_TIER[achievement.tier] : XP_PER_ACHIEVEMENT;
}

/**
 * Total experience needed to reach a level.
 *
 * Each level costs 500 more than the one before: 500 to reach level 2, 1,500 for
 * level 3, 5,000 for level 5, 22,500 for level 10. A first evening of five games
 * gets most people to level 2 or 3; level 10 is somewhere past a hundred games.
 */
export function xpForLevel(level: number): number {
  if (level <= 1) return 0;
  return (500 * (level - 1) * level) / 2;
}

export function levelForXp(xp: number): number {
  let level = 1;
  while (xpForLevel(level + 1) <= xp) level++;
  return level;
}

// ---------------------------------------------------------------------------
// The catalogue
// ---------------------------------------------------------------------------

export type CosmeticKind =
  | 'color'
  | 'emoji'
  | 'plate'
  | 'title'
  | 'flourish'
  | 'skin'
  | 'font'
  | 'frame'
  | 'catchphrase'
  | 'entrance'
  | 'banner';

export type Unlock =
  | { by: 'free' }
  | { by: 'level'; level: number }
  | { by: 'achievement'; id: string }
  /** Only while the event is on, after this many games played during it (often none). */
  | { by: 'season'; season: SeasonId; games: number };

export interface Cosmetic {
  kind: CosmeticKind;
  /** What is stored and broadcast: a hex colour, an emoji, a plate or effect token. */
  value: string;
  name: string;
  unlock: Unlock;
  /** A skin made for one game, which can only be chosen for that game. */
  game?: GameId;
  /** For a catchphrase, the line itself. */
  text?: string;
  /**
   * Worn only during its event, over the top of a player's usual look (see
   * `SeasonalWear`), and never in their ordinary slots.
   */
  season?: SeasonId;
}

const FREE: Unlock = { by: 'free' };
const atLevel = (level: number): Unlock => ({ by: 'level', level });
const forAchievement = (id: string): Unlock => ({ by: 'achievement', id });
const inSeason = (season: SeasonId, games = 0): Unlock => ({ by: 'season', season, games });

/** The reveal effects a player can own. `sparkle` is everyone's, so everyone gets a moment. */
export const FLOURISHES = [
  'sparkle',
  'bubbles',
  'stars',
  'confetti',
  'hearts',
  'spotlight',
  'glitch',
  'lightning',
  'fireworks',
  'crown',
  'shockwave',
  'disco',
  'emoji-rain',
  'lasers',
  'coins',
  'meteor',
  'petals',
  'supernova',
] as const;
export type Flourish = (typeof FLOURISHES)[number];

/** Skins change only the phone they are chosen on. */
export const SKINS = [
  'neon', 'noir', 'vaporwave', 'sunset', 'arcade', 'terminal',
  'midnight', 'mono', 'candy', 'ocean', 'forest', 'synthwave', 'glacier', 'volcano',
  'galaxy', 'cyberpunk', 'gold-rush', 'royal', 'sakura', 'retro-tv', 'platinum',
] as const;
export type Skin = (typeof SKINS)[number];

export interface GameSkin {
  value: string;
  name: string;
  /** An achievement id, or a level. */
  unlock: string | number;
}

/**
 * Three skins per game, dressed for it and earned in it. Like every skin they can
 * be worn anywhere, or picked for any one game.
 */
export const GAME_SKINS: Record<GameId, GameSkin[]> = {
  movie: [
    { value: 'cinema', name: 'Picture House', unlock: 'film-buff' },
    { value: 'drive-in', name: 'Drive-In', unlock: 'chain-gang' },
    { value: 'technicolor', name: 'Technicolor', unlock: 'epic' },
  ],
  quiz: [
    { value: 'gameshow', name: 'Studio Lights', unlock: 'on-a-roll' },
    { value: 'pub-quiz', name: 'Pub Quiz', unlock: 'know-it-all' },
    { value: 'chalkboard', name: 'Chalkboard', unlock: 'unstoppable' },
  ],
  quiplash: [
    { value: 'comedy-club', name: 'Comedy Cellar', unlock: 'crowd-pleaser' },
    { value: 'late-night', name: 'Late Night', unlock: 'quiplash' },
    { value: 'roast', name: 'The Roast', unlock: 'comedian' },
  ],
  fibbage: [
    { value: 'typewriter', name: 'Typewriter', unlock: 'master-of-deceit' },
    { value: 'tabloid', name: 'Tabloid', unlock: 'super-liar' },
    { value: 'interrogation', name: 'Interrogation', unlock: 'truth-seeker' },
  ],
  enough: [
    { value: 'diary', name: 'Dear Diary', unlock: 'open-book' },
    { value: 'polaroid', name: 'Polaroid', unlock: 'two-faced' },
    { value: 'sleepover', name: 'Sleepover', unlock: 'mind-reader' },
  ],
  wavelengths: [
    { value: 'radio', name: 'Night Radio', unlock: 'tuned-in' },
    { value: 'oscilloscope', name: 'Oscilloscope', unlock: 'bullseye' },
    { value: 'northern-lights', name: 'Northern Lights', unlock: 'broadcaster' },
  ],
  obscurity: [
    { value: 'archive', name: 'The Archive', unlock: 'deep-cut' },
    { value: 'museum', name: 'Museum', unlock: 'curious-mind' },
    { value: 'deep-sea', name: 'Deep Sea', unlock: 'obscurantist' },
  ],
  superlatives: [
    { value: 'yearbook', name: 'Yearbook', unlock: 'usual-suspect' },
    { value: 'prom', name: 'Prom Night', unlock: 'hive-mind' },
    { value: 'awards', name: 'Awards Night', unlock: 'crowd-favourite' },
  ],
  chameleon: [
    { value: 'jungle', name: 'Jungle', unlock: 4 },
    { value: 'camouflage', name: 'Camouflage', unlock: 'eagle-eye' },
    { value: 'spy', name: 'Spy HQ', unlock: 'master-of-disguise' },
  ],
  hottakes: [
    { value: 'hot-sauce', name: 'Hot Sauce', unlock: 4 },
    { value: 'debate', name: 'Debate Stage', unlock: 'mind-meld' },
    { value: 'heatwave', name: 'Heatwave', unlock: 'spicy-take' },
  ],
  higherlower: [{ value: 'timeline', name: 'Timeline', unlock: 'on-the-run' }],
  castreveal: [{ value: 'casting-call', name: 'Casting Call', unlock: 'name-drop' }],
  posterzoom: [{ value: 'billboard', name: 'Billboard', unlock: 'sharp-eyed' }],
  sixdegrees: [{ value: 'connections', name: 'Connections', unlock: 'connector' }],
  connect4: [{ value: 'neon-arcade', name: 'Neon Arcade', unlock: 'four-play' }],
  noughts: [{ value: 'graph-paper', name: 'Graph Paper', unlock: 'tic-tac-pro' }],
  battleships: [{ value: 'blueprint', name: 'Blueprint', unlock: 'admiral' }],
  dots: [{ value: 'notebook', name: 'Notebook', unlock: 'box-clever' }],
  wordduel: [{ value: 'letterpress', name: 'Letterpress', unlock: 'wordsmith' }],
  anagrams: [{ value: 'word-tiles', name: 'Word Tiles', unlock: 'anagrammer' }],
  poker: [{ value: 'felt', name: 'Card Room', unlock: 'first-pot' }],
  liarsdice: [{ value: 'tavern', name: 'Tavern', unlock: 'liar-liar' }],
  whosaid: [{ value: 'confessional', name: 'Confessional', unlock: 'people-reader' }],
  wouldyourather: [{ value: 'crossroads', name: 'Crossroads', unlock: 'crowd-reader' }],
  cheat: [{ value: 'backroom', name: 'Back Room', unlock: 'caught-you' }],
  rankem: [{ value: 'podium', name: 'Podium', unlock: 'on-the-same-page' }],
  spotlight: [{ value: 'stage-light', name: 'Stage Light', unlock: 'good-friend' }],
  blindranking: [{ value: 'blindfold', name: 'Blindfold', unlock: 'in-tune' }],
  onestar: [{ value: 'critic', name: 'Critic', unlock: 'harsh-critic' }],
  wagerquiz: [{ value: 'casino', name: 'Casino', unlock: 'sure-thing' }],
  fakinit: [{ value: 'poker-face', name: 'Poker Face', unlock: 'faker-finder' }],
  undercover: [{ value: 'safehouse', name: 'Safehouse', unlock: 'agent' }],
  mole: [{ value: 'burrow', name: 'Burrow', unlock: 'mole-hunter' }],
  boxart: [{ value: 'cartridge', name: 'Cartridge', unlock: 'cartridge-collector' }],
};

/**
 * Typefaces for a name. `house` is the plain one everybody starts with. The rest
 * are Google Fonts loaded by the page, so each value maps to a family there.
 */
export const NAME_FONTS: Record<string, string> = {
  house: 'ui-sans-serif, system-ui, sans-serif',
  sign: "Bungee, ui-sans-serif, sans-serif",
  typewriter: "'Special Elite', ui-monospace, monospace",
  terminal: 'VT323, ui-monospace, monospace',
  script: 'Pacifico, cursive',
  pixel: "'Press Start 2P', ui-monospace, monospace",
  comic: 'Bangers, ui-sans-serif, sans-serif',
  marquee: 'Monoton, ui-serif, serif',
  royal: 'Cinzel, ui-serif, serif',
  horror: 'Creepster, ui-sans-serif, sans-serif',
  spooky: 'Nosifer, ui-sans-serif, sans-serif',
  festive: "'Mountains of Christmas', ui-serif, serif",
  sweet: "'Dancing Script', cursive",
  garden: 'Caveat, cursive',
  surf: "'Bungee Shade', ui-sans-serif, sans-serif",
  round: 'Fredoka, ui-rounded, ui-sans-serif, sans-serif',
  mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  marker: "'Permanent Marker', cursive",
  space: 'Orbitron, ui-sans-serif, sans-serif',
  western: 'Rye, ui-serif, serif',
  retro: 'Righteous, ui-sans-serif, sans-serif',
  elegant: "'Playfair Display', ui-serif, serif",
  bubble: "'Rubik Bubbles', ui-sans-serif, sans-serif",
  gothic: "'UnifrakturMaguntia', ui-serif, serif",
};

/** Scale for fonts that run much wider or narrower than the house face. */
export const NAME_FONT_SCALE: Record<string, number> = {
  pixel: 0.62,
  marquee: 0.85,
  terminal: 1.25,
  comic: 1.1,
  spooky: 0.78,
  surf: 0.9,
  garden: 1.2,
  sweet: 1.1,
  space: 0.85,
  western: 0.85,
  bubble: 0.95,
  gothic: 1.05,
};

export const FRAMES = [
  'none', 'ring', 'double', 'dashed', 'dotted', 'thick', 'glow', 'spin', 'rainbow', 'gold', 'frost', 'ember',
  'halo', 'pulse', 'electric', 'orbit', 'galaxy', 'leaf', 'diamond', 'platinum',
] as const;
export const ENTRANCES = [
  'fade', 'rise', 'slide', 'pop', 'bounce', 'drop', 'spin', 'flip', 'zoom', 'shake', 'glitch', 'spotlight',
  'portal', 'thunder', 'warp', 'royal',
] as const;

/** Everything each event brings, worn over the top of a player's usual look. */
export const SEASONAL_COSMETICS: Cosmetic[] = [
  // Halloween
  { kind: 'skin', value: 'pumpkin', name: 'Pumpkin Patch', unlock: inSeason('halloween'), season: 'halloween' },
  { kind: 'frame', value: 'cobweb', name: 'Cobweb', unlock: inSeason('halloween'), season: 'halloween' },
  { kind: 'frame', value: 'jack', name: "Jack-o'-lantern", unlock: inSeason('halloween', 5), season: 'halloween' },
  { kind: 'entrance', value: 'ghost', name: 'Ghostly', unlock: inSeason('halloween'), season: 'halloween' },
  { kind: 'entrance', value: 'bats', name: 'Bat Swarm', unlock: inSeason('halloween'), season: 'halloween' },
  { kind: 'font', value: 'spooky', name: 'Dripping', unlock: inSeason('halloween'), season: 'halloween' },
  { kind: 'catchphrase', value: 'boo', name: 'Boo', text: 'Boo. Did I scare you?', unlock: inSeason('halloween'), season: 'halloween' },
  // Christmas
  { kind: 'skin', value: 'tinsel', name: 'Tinsel Town', unlock: inSeason('christmas'), season: 'christmas' },
  { kind: 'frame', value: 'wreath', name: 'Wreath', unlock: inSeason('christmas'), season: 'christmas' },
  { kind: 'frame', value: 'snowglobe', name: 'Snow Globe', unlock: inSeason('christmas', 5), season: 'christmas' },
  { kind: 'entrance', value: 'snowfall', name: 'Snowfall', unlock: inSeason('christmas'), season: 'christmas' },
  { kind: 'entrance', value: 'sleigh', name: 'Sleigh Ride', unlock: inSeason('christmas'), season: 'christmas' },
  { kind: 'font', value: 'festive', name: 'Festive', unlock: inSeason('christmas'), season: 'christmas' },
  { kind: 'catchphrase', value: 'gift', name: 'A gift', text: 'Consider this my present to you.', unlock: inSeason('christmas'), season: 'christmas' },
  // Valentine's
  { kind: 'skin', value: 'sweetheart', name: 'Sweetheart', unlock: inSeason('valentines'), season: 'valentines' },
  { kind: 'frame', value: 'hearts', name: 'Hearts', unlock: inSeason('valentines'), season: 'valentines' },
  { kind: 'frame', value: 'cupid', name: "Cupid's Arrow", unlock: inSeason('valentines', 3), season: 'valentines' },
  { kind: 'entrance', value: 'heartbeat', name: 'Heartbeat', unlock: inSeason('valentines'), season: 'valentines' },
  { kind: 'entrance', value: 'arrow', name: 'Arrow', unlock: inSeason('valentines'), season: 'valentines' },
  { kind: 'font', value: 'sweet', name: 'Love Letter', unlock: inSeason('valentines'), season: 'valentines' },
  { kind: 'catchphrase', value: 'love', name: 'Love you all', text: 'Love you all. Still won though.', unlock: inSeason('valentines'), season: 'valentines' },
  // Spring
  { kind: 'skin', value: 'blossom', name: 'Blossom', unlock: inSeason('spring'), season: 'spring' },
  { kind: 'frame', value: 'petals', name: 'Petals', unlock: inSeason('spring'), season: 'spring' },
  { kind: 'frame', value: 'bloom', name: 'Full Bloom', unlock: inSeason('spring', 5), season: 'spring' },
  { kind: 'entrance', value: 'sprout', name: 'Sprout', unlock: inSeason('spring'), season: 'spring' },
  { kind: 'entrance', value: 'flutter', name: 'Flutter', unlock: inSeason('spring'), season: 'spring' },
  { kind: 'font', value: 'garden', name: 'Garden Party', unlock: inSeason('spring'), season: 'spring' },
  { kind: 'catchphrase', value: 'daisy', name: 'Fresh', text: 'Fresh as a daisy.', unlock: inSeason('spring'), season: 'spring' },
  // Summer
  { kind: 'skin', value: 'beach', name: 'Beach Party', unlock: inSeason('summer'), season: 'summer' },
  { kind: 'frame', value: 'sunny', name: 'Sunshine', unlock: inSeason('summer'), season: 'summer' },
  { kind: 'frame', value: 'lifebuoy', name: 'Lifebuoy', unlock: inSeason('summer', 5), season: 'summer' },
  { kind: 'entrance', value: 'splash', name: 'Splash', unlock: inSeason('summer'), season: 'summer' },
  { kind: 'entrance', value: 'surf', name: 'Surf In', unlock: inSeason('summer'), season: 'summer' },
  { kind: 'font', value: 'surf', name: 'Surf Shack', unlock: inSeason('summer'), season: 'summer' },
  { kind: 'catchphrase', value: 'sunny', name: 'Too hot', text: 'Too hot to lose.', unlock: inSeason('summer'), season: 'summer' },
];

export function catchphraseText(id: string | null | undefined): string | null {
  if (!id) return null;
  return cosmetic('catchphrase', id)?.text ?? null;
}

export const COSMETICS: Cosmetic[] = [
  // Colours: the original eight are free, forever.
  ...NEON_COLORS.map((value) => ({ kind: 'color' as const, value, name: value, unlock: FREE })),
  { kind: 'color', value: '#ff9ef0', name: 'Bubblegum', unlock: atLevel(3) },
  { kind: 'color', value: '#ffffff', name: 'White Hot', unlock: atLevel(5) },
  { kind: 'color', value: '#a8d8ff', name: 'Ice', unlock: atLevel(8) },
  { kind: 'color', value: '#d4ff3f', name: 'Acid', unlock: atLevel(11) },

  // Emoji: every one that existed before stays free.
  ...PLAYER_EMOJI.map((value) => ({ kind: 'emoji' as const, value, name: value, unlock: FREE })),
  { kind: 'emoji', value: '🍀', name: 'Four-leaf clover', unlock: atLevel(2) },
  { kind: 'emoji', value: '👾', name: 'Space invader', unlock: atLevel(4) },
  { kind: 'emoji', value: '🌟', name: 'Star', unlock: atLevel(7) },
  { kind: 'emoji', value: '🐉', name: 'Dragon', unlock: atLevel(10) },
  { kind: 'emoji', value: '🧿', name: 'Evil eye', unlock: atLevel(13) },
  { kind: 'emoji', value: '🪩', name: 'Disco ball', unlock: atLevel(16) },
  { kind: 'emoji', value: '👑', name: 'Crown', unlock: forAchievement('champion') },
  { kind: 'emoji', value: '🐐', name: 'Goat', unlock: forAchievement('dynasty') },
  { kind: 'emoji', value: '🦄', name: 'Unicorn', unlock: forAchievement('one-in-a-million') },
  { kind: 'emoji', value: '💎', name: 'Diamond', unlock: forAchievement('deep-cut') },
  { kind: 'emoji', value: '🎯', name: 'Bullseye', unlock: forAchievement('bullseye') },
  { kind: 'emoji', value: '🤥', name: 'Pinocchio', unlock: forAchievement('super-liar') },
  { kind: 'emoji', value: '🎭', name: 'Masks', unlock: forAchievement('master-of-deceit') },
  { kind: 'emoji', value: '🧠', name: 'Brain', unlock: forAchievement('know-it-all') },
  { kind: 'emoji', value: '🔥', name: 'Fire', unlock: forAchievement('on-a-roll') },

  // Plates: the original eight are free; the moving ones are earned.
  ...PLAYER_PLATES.map((value) => ({ kind: 'plate' as const, value, name: value, unlock: FREE })),
  { kind: 'plate', value: 'shimmer', name: 'Shimmer', unlock: atLevel(2) },
  { kind: 'plate', value: 'pulse', name: 'Pulse', unlock: atLevel(4) },
  { kind: 'plate', value: 'scan', name: 'Scanlines', unlock: atLevel(6) },
  { kind: 'plate', value: 'aurora', name: 'Aurora', unlock: atLevel(9) },
  { kind: 'plate', value: 'inferno', name: 'Inferno', unlock: atLevel(12) },
  { kind: 'plate', value: 'gold', name: 'Gold', unlock: forAchievement('champion') },
  { kind: 'plate', value: 'holo', name: 'Holo', unlock: forAchievement('one-in-a-million') },

  // Titles: each achievement is its own title.
  ...ACHIEVEMENTS.map((a) => ({
    kind: 'title' as const,
    value: a.id,
    name: a.name,
    unlock: forAchievement(a.id),
  })),

  // Reveal flourishes.
  { kind: 'flourish', value: 'sparkle', name: 'Sparkle', unlock: FREE },
  { kind: 'flourish', value: 'confetti', name: 'Confetti', unlock: atLevel(2) },
  { kind: 'flourish', value: 'spotlight', name: 'Spotlight', unlock: atLevel(5) },
  { kind: 'flourish', value: 'glitch', name: 'Glitch', unlock: atLevel(8) },
  { kind: 'flourish', value: 'lightning', name: 'Lightning', unlock: forAchievement('super-liar') },
  { kind: 'flourish', value: 'fireworks', name: 'Fireworks', unlock: forAchievement('champion') },
  { kind: 'flourish', value: 'crown', name: 'Crowned', unlock: forAchievement('dynasty') },

  // Skins.
  { kind: 'skin', value: 'neon', name: 'Neon', unlock: FREE },
  { kind: 'skin', value: 'noir', name: 'Noir', unlock: atLevel(3) },
  { kind: 'skin', value: 'vaporwave', name: 'Vaporwave', unlock: atLevel(6) },
  { kind: 'skin', value: 'sunset', name: 'Sunset Drive', unlock: atLevel(9) },
  { kind: 'skin', value: 'arcade', name: 'Arcade', unlock: atLevel(12) },
  { kind: 'skin', value: 'terminal', name: 'Terminal', unlock: forAchievement('know-it-all') },
  { kind: 'skin', value: 'midnight', name: 'Midnight', unlock: FREE },
  { kind: 'skin', value: 'mono', name: 'Monochrome', unlock: FREE },
  { kind: 'skin', value: 'candy', name: 'Candy Shop', unlock: atLevel(2) },
  { kind: 'skin', value: 'ocean', name: 'Open Ocean', unlock: atLevel(4) },
  { kind: 'skin', value: 'forest', name: 'Enchanted Forest', unlock: atLevel(5) },
  { kind: 'skin', value: 'synthwave', name: 'Synthwave', unlock: atLevel(7) },
  { kind: 'skin', value: 'glacier', name: 'Glacier', unlock: atLevel(10) },
  { kind: 'skin', value: 'volcano', name: 'Volcano', unlock: atLevel(13) },
  { kind: 'skin', value: 'galaxy', name: 'Galaxy', unlock: atLevel(16) },
  { kind: 'skin', value: 'cyberpunk', name: 'Cyberpunk', unlock: atLevel(20) },
  { kind: 'skin', value: 'retro-tv', name: 'Retro TV', unlock: forAchievement('night-owl') },
  { kind: 'skin', value: 'sakura', name: 'Sakura', unlock: forAchievement('social-butterfly') },
  { kind: 'skin', value: 'royal', name: 'Royal Court', unlock: forAchievement('dynasty') },
  { kind: 'skin', value: 'gold-rush', name: 'Gold Rush', unlock: forAchievement('legend') },
  { kind: 'skin', value: 'platinum', name: 'Platinum', unlock: forAchievement('hall-of-fame') },

  // Earned extras across the old kinds, for the new achievements.
  { kind: 'emoji', value: '🏅', name: 'Medal', unlock: forAchievement('podium') },
  { kind: 'emoji', value: '🦋', name: 'Butterfly', unlock: forAchievement('social-butterfly') },
  { kind: 'emoji', value: '🌙', name: 'Moon', unlock: forAchievement('night-owl') },
  { kind: 'emoji', value: '🥄', name: 'Wooden spoon', unlock: forAchievement('good-sport') },
  { kind: 'emoji', value: '🎙️', name: 'Microphone', unlock: forAchievement('headliner') },
  { kind: 'emoji', value: '🕵️', name: 'Detective', unlock: forAchievement('lie-detector') },
  { kind: 'emoji', value: '📻', name: 'Radio', unlock: forAchievement('broadcaster') },
  { kind: 'emoji', value: '📜', name: 'Scroll', unlock: forAchievement('archivist') },
  { kind: 'emoji', value: '🌋', name: 'Volcano', unlock: atLevel(19) },
  { kind: 'emoji', value: '🌌', name: 'Galaxy', unlock: atLevel(22) },
  { kind: 'color', value: '#ffd700', name: 'Gold Leaf', unlock: forAchievement('legend') },
  { kind: 'color', value: '#ff5e00', name: 'Ember', unlock: atLevel(14) },

  // Name fonts.
  { kind: 'font', value: 'house', name: 'Plain', unlock: FREE },
  { kind: 'font', value: 'sign', name: 'Sign Writer', unlock: FREE },
  { kind: 'font', value: 'typewriter', name: 'Typewriter', unlock: atLevel(3) },
  { kind: 'font', value: 'terminal', name: 'Terminal', unlock: atLevel(4) },
  { kind: 'font', value: 'script', name: 'Script', unlock: atLevel(6) },
  { kind: 'font', value: 'pixel', name: 'Pixel', unlock: atLevel(8) },
  { kind: 'font', value: 'marquee', name: 'Marquee', unlock: atLevel(11) },
  { kind: 'font', value: 'comic', name: 'Comic Strip', unlock: forAchievement('comedian') },
  { kind: 'font', value: 'royal', name: 'Royal', unlock: forAchievement('legend') },
  { kind: 'font', value: 'horror', name: 'Horror', unlock: forAchievement('good-sport') },

  // Frames around the avatar.
  { kind: 'frame', value: 'none', name: 'None', unlock: FREE },
  { kind: 'frame', value: 'ring', name: 'Ring', unlock: FREE },
  { kind: 'frame', value: 'double', name: 'Double', unlock: FREE },
  { kind: 'frame', value: 'dashed', name: 'Dashed', unlock: atLevel(2) },
  { kind: 'frame', value: 'glow', name: 'Glow', unlock: atLevel(3) },
  { kind: 'frame', value: 'spin', name: 'Orbit', unlock: atLevel(6) },
  { kind: 'frame', value: 'rainbow', name: 'Rainbow', unlock: atLevel(9) },
  { kind: 'frame', value: 'gold', name: 'Gold Ring', unlock: forAchievement('dynasty') },
  { kind: 'frame', value: 'frost', name: 'Frost', unlock: forAchievement('mastermind') },
  { kind: 'frame', value: 'ember', name: 'Ember', unlock: forAchievement('unstoppable') },

  // Catchphrases, shown under your name when you win.
  { kind: 'catchphrase', value: 'gg', name: 'Good game', text: 'Good game, everyone.', unlock: FREE },
  { kind: 'catchphrase', value: 'surprised', name: 'Surprised', text: "Honestly, I'm as surprised as you.", unlock: FREE },
  { kind: 'catchphrase', value: 'easy', name: 'Was that it?', text: 'Was that it?', unlock: atLevel(2) },
  { kind: 'catchphrase', value: 'again', name: 'Same time', text: 'Same time next week?', unlock: atLevel(3) },
  { kind: 'catchphrase', value: 'agent', name: 'Speech', text: "I'd like to thank my agent.", unlock: atLevel(4) },
  { kind: 'catchphrase', value: 'friends', name: 'Tell your friends', text: 'Tell your friends.', unlock: atLevel(6) },
  { kind: 'catchphrase', value: 'mic', name: 'Mic drop', text: '🎤 ⬇️', unlock: atLevel(8) },
  { kind: 'catchphrase', value: 'nobody', name: 'Unstoppable', text: 'Nobody can stop me. Nobody.', unlock: forAchievement('unstoppable') },
  { kind: 'catchphrase', value: 'true', name: 'All true', text: 'Every word of it was true.', unlock: forAchievement('con-artist') },
  { kind: 'catchphrase', value: 'always', name: 'Always me', text: 'It was always going to be me.', unlock: forAchievement('main-character') },
  { kind: 'catchphrase', value: 'again-we-meet', name: 'We meet again', text: 'We meet again.', unlock: forAchievement('nemesis') },
  { kind: 'catchphrase', value: 'hungry', name: 'Snack break', text: 'Is anyone else hungry?', unlock: forAchievement('marathon') },
  { kind: 'catchphrase', value: 'winning', name: 'Good sport', text: "Winning isn't everything. But this was nice.", unlock: forAchievement('good-sport') },

  // How your card arrives in a lobby.
  { kind: 'entrance', value: 'fade', name: 'Fade in', unlock: FREE },
  { kind: 'entrance', value: 'slide', name: 'Slide', unlock: atLevel(2) },
  { kind: 'entrance', value: 'pop', name: 'Pop', unlock: atLevel(3) },
  { kind: 'entrance', value: 'drop', name: 'Drop in', unlock: atLevel(5) },
  { kind: 'entrance', value: 'spin', name: 'Spin', unlock: atLevel(7) },
  { kind: 'entrance', value: 'glitch', name: 'Glitch', unlock: atLevel(10) },
  { kind: 'entrance', value: 'spotlight', name: 'Spotlight', unlock: forAchievement('party-animal') },
  { kind: 'entrance', value: 'thunder', name: 'Thunder', unlock: forAchievement('legend') },

  // More flourishes: two more for everyone, the rest earned.
  { kind: 'flourish', value: 'bubbles', name: 'Bubbles', unlock: FREE },
  { kind: 'flourish', value: 'stars', name: 'Shooting Stars', unlock: FREE },
  { kind: 'flourish', value: 'hearts', name: 'Hearts', unlock: atLevel(3) },
  { kind: 'flourish', value: 'shockwave', name: 'Shockwave', unlock: atLevel(6) },
  { kind: 'flourish', value: 'disco', name: 'Disco', unlock: atLevel(10) },
  { kind: 'flourish', value: 'emoji-rain', name: 'Emoji Rain', unlock: atLevel(13) },
  { kind: 'flourish', value: 'lasers', name: 'Laser Show', unlock: forAchievement('headliner') },
  { kind: 'flourish', value: 'coins', name: 'Jackpot', unlock: forAchievement('know-it-all') },
  { kind: 'flourish', value: 'meteor', name: 'Meteor', unlock: forAchievement('master-of-disguise') },
  { kind: 'flourish', value: 'petals', name: 'Petals', unlock: forAchievement('mind-meld') },
  { kind: 'flourish', value: 'supernova', name: 'Supernova', unlock: forAchievement('immortal') },

  // More plates: three more for everyone, the rest earned.
  { kind: 'plate', value: 'dots', name: 'Dots', unlock: FREE },
  { kind: 'plate', value: 'waves', name: 'Waves', unlock: FREE },
  { kind: 'plate', value: 'chevron', name: 'Chevron', unlock: FREE },
  { kind: 'plate', value: 'checker', name: 'Checker', unlock: atLevel(3) },
  { kind: 'plate', value: 'lava', name: 'Lava Lamp', unlock: atLevel(7) },
  { kind: 'plate', value: 'matrix', name: 'Matrix', unlock: atLevel(15) },
  { kind: 'plate', value: 'galaxy', name: 'Galaxy', unlock: atLevel(18) },
  { kind: 'plate', value: 'prism', name: 'Prism', unlock: forAchievement('grand-tour') },
  { kind: 'plate', value: 'camo', name: 'Camo', unlock: forAchievement('eagle-eye') },
  { kind: 'plate', value: 'flames', name: 'Flames', unlock: forAchievement('spicy-take') },
  { kind: 'plate', value: 'platinum', name: 'Platinum', unlock: forAchievement('hall-of-fame') },

  // More name fonts.
  { kind: 'font', value: 'round', name: 'Rounded', unlock: FREE },
  { kind: 'font', value: 'mono', name: 'Monospace', unlock: FREE },
  { kind: 'font', value: 'marker', name: 'Marker', unlock: atLevel(5) },
  { kind: 'font', value: 'retro', name: 'Retro', unlock: atLevel(7) },
  { kind: 'font', value: 'space', name: 'Space Age', unlock: atLevel(9) },
  { kind: 'font', value: 'elegant', name: 'Elegant', unlock: atLevel(12) },
  { kind: 'font', value: 'western', name: 'Wild West', unlock: forAchievement('lizard-king') },
  { kind: 'font', value: 'bubble', name: 'Bubble', unlock: forAchievement('hot-shot') },
  { kind: 'font', value: 'gothic', name: 'Gothic', unlock: forAchievement('monument') },

  // More frames.
  { kind: 'frame', value: 'dotted', name: 'Dotted', unlock: FREE },
  { kind: 'frame', value: 'thick', name: 'Bold', unlock: FREE },
  { kind: 'frame', value: 'halo', name: 'Halo', unlock: atLevel(5) },
  { kind: 'frame', value: 'pulse', name: 'Heartbeat', unlock: atLevel(8) },
  { kind: 'frame', value: 'electric', name: 'Electric', unlock: atLevel(14) },
  { kind: 'frame', value: 'orbit', name: 'Satellites', unlock: forAchievement('invisible') },
  { kind: 'frame', value: 'galaxy', name: 'Galaxy', unlock: forAchievement('pollster') },
  { kind: 'frame', value: 'leaf', name: 'Laurels', unlock: forAchievement('team-player') },
  { kind: 'frame', value: 'diamond', name: 'Diamond', unlock: forAchievement('week-warrior') },
  { kind: 'frame', value: 'platinum', name: 'Platinum', unlock: forAchievement('immortal') },

  // Banners: a wide backdrop behind your name card.
  { kind: 'banner', value: 'none', name: 'None', unlock: FREE },
  { kind: 'banner', value: 'stripes', name: 'Stripes', unlock: FREE },
  { kind: 'banner', value: 'dots', name: 'Polka Dots', unlock: FREE },
  { kind: 'banner', value: 'grid', name: 'Grid', unlock: FREE },
  { kind: 'banner', value: 'confetti', name: 'Confetti', unlock: atLevel(2) },
  { kind: 'banner', value: 'film-strip', name: 'Film Strip', unlock: atLevel(3) },
  { kind: 'banner', value: 'waves', name: 'Waves', unlock: atLevel(4) },
  { kind: 'banner', value: 'checker', name: 'Checkered Flag', unlock: atLevel(5) },
  { kind: 'banner', value: 'sunburst', name: 'Sunburst', unlock: atLevel(6) },
  { kind: 'banner', value: 'zigzag', name: 'Zigzag', unlock: atLevel(7) },
  { kind: 'banner', value: 'city', name: 'City Lights', unlock: atLevel(8) },
  { kind: 'banner', value: 'stage', name: 'Stage Curtain', unlock: atLevel(9) },
  { kind: 'banner', value: 'arcade', name: 'Arcade Floor', unlock: atLevel(11) },
  { kind: 'banner', value: 'space', name: 'Deep Space', unlock: atLevel(12) },
  { kind: 'banner', value: 'circuit', name: 'Circuit Board', unlock: atLevel(14) },
  { kind: 'banner', value: 'mosaic', name: 'Mosaic', unlock: atLevel(15) },
  { kind: 'banner', value: 'aurora', name: 'Aurora', unlock: atLevel(18) },
  { kind: 'banner', value: 'rain', name: 'Neon Rain', unlock: atLevel(22) },
  { kind: 'banner', value: 'equaliser', name: 'Equaliser', unlock: atLevel(25) },
  { kind: 'banner', value: 'spotlights', name: 'Spotlights', unlock: forAchievement('party-animal') },
  { kind: 'banner', value: 'starfield', name: 'Starfield', unlock: forAchievement('night-owl') },
  { kind: 'banner', value: 'trophies', name: 'Trophy Cabinet', unlock: forAchievement('dynasty') },
  { kind: 'banner', value: 'fireworks', name: 'Fireworks', unlock: forAchievement('legend') },
  { kind: 'banner', value: 'lava', name: 'Lava Lamp', unlock: forAchievement('hall-of-fame') },
  { kind: 'banner', value: 'scales', name: 'Scales', unlock: forAchievement('lizard-king') },
  { kind: 'banner', value: 'flames', name: 'Flames', unlock: forAchievement('firestarter') },
  { kind: 'banner', value: 'cobwebs', name: 'Cobwebs', unlock: inSeason('halloween'), season: 'halloween' },
  { kind: 'banner', value: 'snowflakes', name: 'Snowflakes', unlock: inSeason('christmas'), season: 'christmas' },
  { kind: 'banner', value: 'hearts', name: 'Hearts', unlock: inSeason('valentines'), season: 'valentines' },
  { kind: 'banner', value: 'petals', name: 'Petals', unlock: inSeason('spring'), season: 'spring' },
  { kind: 'banner', value: 'palms', name: 'Palm Shade', unlock: inSeason('summer', 3), season: 'summer' },

  // More entrances.
  { kind: 'entrance', value: 'rise', name: 'Rise', unlock: FREE },
  { kind: 'entrance', value: 'bounce', name: 'Bounce', unlock: FREE },
  { kind: 'entrance', value: 'flip', name: 'Flip', unlock: atLevel(4) },
  { kind: 'entrance', value: 'zoom', name: 'Zoom', unlock: atLevel(6) },
  { kind: 'entrance', value: 'shake', name: 'Shake', unlock: atLevel(9) },
  { kind: 'entrance', value: 'portal', name: 'Portal', unlock: forAchievement('phantom') },
  { kind: 'entrance', value: 'warp', name: 'Warp Speed', unlock: forAchievement('habit-forming') },
  { kind: 'entrance', value: 'royal', name: 'Royal Arrival', unlock: forAchievement('hall-of-fame') },

  // Earned extras from the new games and the platinum steps.
  { kind: 'emoji', value: '🦎', name: 'Chameleon', unlock: forAchievement('master-of-disguise') },
  { kind: 'emoji', value: '👁️', name: 'Eye', unlock: forAchievement('eagle-eye') },
  { kind: 'emoji', value: '🥷', name: 'Ninja', unlock: forAchievement('phantom') },
  { kind: 'emoji', value: '🌶️', name: 'Chilli', unlock: forAchievement('spicy-take') },
  { kind: 'emoji', value: '🪄', name: 'Magic wand', unlock: forAchievement('mind-meld') },
  { kind: 'emoji', value: '🧯', name: 'Extinguisher', unlock: forAchievement('hot-shot') },
  { kind: 'emoji', value: '📅', name: 'Calendar', unlock: forAchievement('habit-forming') },
  { kind: 'emoji', value: '🤝', name: 'Handshake', unlock: forAchievement('team-player') },
  { kind: 'emoji', value: '🏛️', name: 'Temple', unlock: forAchievement('hall-of-fame') },
  { kind: 'emoji', value: '💠', name: 'Platinum', unlock: forAchievement('immortal') },
  { kind: 'color', value: '#e5e4e2', name: 'Platinum', unlock: forAchievement('hall-of-fame') },
  { kind: 'color', value: '#7fffd4', name: 'Aquamarine', unlock: forAchievement('pollster') },
  { kind: 'color', value: '#9acd32', name: 'Lizard', unlock: forAchievement('invisible') },
  { kind: 'catchphrase', value: 'chameleon', name: 'Blend in', text: 'You never saw me coming.', unlock: forAchievement('master-of-disguise') },
  { kind: 'catchphrase', value: 'hot-take', name: 'Hot take', text: 'Hot take: I deserved that.', unlock: forAchievement('spicy-take') },
  { kind: 'catchphrase', value: 'streak', name: 'Every day', text: 'I do this every day, you know.', unlock: forAchievement('week-warrior') },
  { kind: 'catchphrase', value: 'platinum', name: 'Platinum', text: 'Some of us are just built different.', unlock: forAchievement('hall-of-fame') },

  // Skins for each game, earned in that game.
  ...Object.entries(GAME_SKINS).flatMap(([game, skins]) =>
    skins.map((skin) => ({
      kind: 'skin' as const,
      value: skin.value,
      name: skin.name,
      unlock: typeof skin.unlock === 'number' ? atLevel(skin.unlock) : forAchievement(skin.unlock),
      game: game as GameId,
    })),
  ),

  ...SEASONAL_COSMETICS,
];

export function cosmetic(kind: CosmeticKind, value: string): Cosmetic | undefined {
  return COSMETICS.find((c) => c.kind === kind && c.value === value);
}

/** What a locked item asks of you, in words. */
export function unlockLabel(unlock: Unlock): string {
  if (unlock.by === 'free') return 'Free';
  if (unlock.by === 'level') return `Reach level ${unlock.level}`;
  if (unlock.by === 'season') {
    return unlock.games > 0 ? `Play ${unlock.games} games during the event` : 'Free during the event';
  }
  const achievement = achievementById(unlock.id);
  return achievement ? `${achievement.name}: ${achievement.description.toLowerCase()}` : 'An achievement';
}

// ---------------------------------------------------------------------------
// Progress
// ---------------------------------------------------------------------------

export interface AchievementProgress {
  id: string;
  name: string;
  description: string;
  game: GameId | null;
  earned: boolean;
  /** Capped at the target, so a bar never overflows. */
  progress: number;
  target: number;
  family?: string;
  tier?: Tier;
  /** Experience it is worth. */
  xp: number;
}

export interface Progress {
  xp: number;
  level: number;
  /** Experience at which the current level began. */
  levelStartXp: number;
  /** Experience at which the next level begins. */
  nextLevelXp: number;
  gamesPlayed: number;
  victories: number;
  /** Games played during the event on now, for the event's own unlocks. */
  seasonGames?: number;
  achievements: AchievementProgress[];
  /** Every cosmetic they own, as `kind:value` keys. */
  unlocked: string[];
}

export function cosmeticKey(kind: CosmeticKind, value: string): string {
  return `${kind}:${value}`;
}

export function computeProgress(stats: StatsSnapshot, now: number = Date.now()): Progress {
  const achievements: AchievementProgress[] = ACHIEVEMENTS.map((a) => {
    const measured = a.measure(stats);
    return {
      id: a.id,
      name: a.name,
      description: a.description,
      game: a.game,
      earned: measured >= a.target,
      progress: Math.min(measured, a.target),
      target: a.target,
      ...(a.family ? { family: a.family } : {}),
      ...(a.tier ? { tier: a.tier } : {}),
      xp: xpFor(a),
    };
  });

  const earned = new Set(achievements.filter((a) => a.earned).map((a) => a.id));
  const gamesPlayed = totalGames(stats);
  const victories = totalVictories(stats);
  const xp =
    gamesPlayed * XP_PER_GAME +
    victories * XP_PER_VICTORY +
    stats.soloGames * XP_PER_SOLO +
    stats.dailyXp +
    achievements.filter((a) => a.earned).reduce((sum, a) => sum + a.xp, 0);
  const level = levelForXp(xp);

  const season = currentSeason(now)?.id ?? null;
  const unlocked = COSMETICS.filter((c) => {
    if (c.unlock.by === 'free') return true;
    if (c.unlock.by === 'level') return level >= c.unlock.level;
    if (c.unlock.by === 'season') return c.unlock.season === season && stats.seasonGames >= c.unlock.games;
    return earned.has(c.unlock.id);
  }).map((c) => cosmeticKey(c.kind, c.value));

  return {
    xp,
    level,
    levelStartXp: xpForLevel(level),
    nextLevelXp: xpForLevel(level + 1),
    gamesPlayed,
    victories,
    seasonGames: stats.seasonGames,
    achievements,
    unlocked,
  };
}

/** How far along somebody is towards an unlock, for a progress bar. */
export interface UnlockProgress {
  current: number;
  target: number;
  /** What it asks for: "Reach level 9", "Spicy Take". */
  label: string;
  /** Where they are: "You're level 4 · 1,200 XP to go", "7 of 10". */
  detail: string;
  done: boolean;
}

export function unlockProgress(progress: Progress | null, unlock: Unlock): UnlockProgress {
  if (unlock.by === 'free') {
    return { current: 1, target: 1, label: 'Free', detail: 'Everyone has this one', done: true };
  }
  if (unlock.by === 'level') {
    const xp = progress?.xp ?? 0;
    const target = xpForLevel(unlock.level);
    const level = progress?.level ?? 1;
    const done = level >= unlock.level;
    return {
      current: Math.min(xp, target),
      target,
      label: `Reach level ${unlock.level}`,
      detail: done
        ? `You're level ${level}`
        : `You're level ${level} · ${(target - xp).toLocaleString('en-GB')} XP to go`,
      done,
    };
  }
  if (unlock.by === 'season') {
    const played = progress?.seasonGames ?? 0;
    const target = Math.max(1, unlock.games);
    const current = unlock.games === 0 ? 1 : Math.min(played, unlock.games);
    return {
      current,
      target,
      label: unlock.games > 0 ? `Play ${unlock.games} games during the event` : 'Free during the event',
      detail: unlock.games > 0 ? `${Math.min(played, unlock.games)} of ${unlock.games} played` : 'Yours while it lasts',
      done: current >= target,
    };
  }
  const achievement = achievementById(unlock.id);
  const mine = progress?.achievements.find((a) => a.id === unlock.id);
  const target = achievement?.target ?? 1;
  const current = mine?.progress ?? 0;
  return {
    current,
    target,
    label: achievement?.name ?? 'An achievement',
    detail: `${achievement?.description ?? ''}${achievement ? ' · ' : ''}${current.toLocaleString('en-GB')} of ${target.toLocaleString('en-GB')}`,
    done: mine?.earned ?? false,
  };
}

/**
 * Whether a player may use an item. A guest has no progress, and gets the free
 * set — which is everything that existed before unlocks did.
 */
export function canUse(progress: Progress | null, kind: CosmeticKind, value: string): boolean {
  const item = cosmetic(kind, value);
  if (!item) return false;
  if (item.unlock.by === 'free') return true;
  return progress?.unlocked.includes(cosmeticKey(kind, value)) ?? false;
}
